import {IEvent, ICalendarEvent, DEPARTMENTS} from "../data/interfaces";
import {buildEventTitle} from "./buildEventTitle";
import {getColor} from "./eventColors";
import {getAssemblyTravelerVisualState} from "./assemblyTravelerStatus";
import {getTravelerVisualColors, TRAVELER_OVERDUE_VISUAL_STATE} from "./travelerVisualColors";
import {getProductionEventColor} from "./productionEventColors";
import {isProductionTravelerOverdue} from "./productionTravelerStatus";

type CalendarColor = {
  backgroundColor: string;
  borderColor?: string;
  textColor: string;
};

/**
 * Function to augment an event object for use in the calendar.
 * @param event
 */
export function augmentEventForCalendar(event: IEvent): ICalendarEvent {
  const assemblyVisualState = event.department === DEPARTMENTS.assembly && event.traveler
    ? getAssemblyTravelerVisualState(
      event.traveler.status,
      event.traveler.readyToShipDeadline,
      event.start
    )
    : null;
  const isProductionOverdue = event.department === DEPARTMENTS.production && event.traveler
    ? isProductionTravelerOverdue(event.traveler.readyToShipDeadline, event.start)
    : false;

  let color: CalendarColor;
  if (assemblyVisualState) {
    color = getTravelerVisualColors(assemblyVisualState);
  } else if (isProductionOverdue) {
    color = getTravelerVisualColors(TRAVELER_OVERDUE_VISUAL_STATE);
  } else if (event.department === DEPARTMENTS.production) {
    color = getProductionEventColor(event.productionEventColor);
  } else {
    color = getColor(event.eventType);
  }

  return {
    ...event,
    end: adjustEndDateForCalendar(event.end),
    title: buildEventTitle(event),
    resourceId: event.crew?.preferredName || 'unassigned',
    backgroundColor: color.backgroundColor,
    textColor: color.textColor,
    borderColor: color.borderColor ?? color.backgroundColor,
  };
}

/**
 * Function to adjust the end date of an event for use in the calendar.
 * @param endDate
 */
function adjustEndDateForCalendar(endDate: string | Date | undefined): string {
  if (!endDate) return '';
  const date = endDate instanceof Date ? endDate : new Date(endDate);
  date.setDate(date.getDate() + 1);
  return date.toISOString().split('T')[0];
}

/**
 * Function to adjust the end date of an event for use in Firestore.
 * @param endDate
 */
export function adjustEndDateForFirestore(endDate: string | Date | undefined): string {
  if (!endDate) return '';
  const date = endDate instanceof Date ? endDate : new Date(endDate);
  date.setDate(date.getDate() - 1);
  return date.toISOString().split('T')[0];
}
